import RenderComponent from '@/modules/DynamicRenderPage';
import { COOKIE_AUTH_DETAILS, pageUrls, ROLES } from 'globalconfig/constants/keys';
import { getCookie } from 'globalservices/cookie';
import { getNewObject, hasEnabledFeature, hasRestrictedRole, loadPageConfig, loadStyle } from 'globalutils/fmUtils';
import PropTypes from 'prop-types';
import React, { lazy, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { allTabs, basicRequest, sortColumnMapping } from './config';
import ParentStyled from './style';

const Styled = loadStyle(ParentStyled, 'pages/web/CompletedInspections');
const layout = window?.$environment?.CURRENT_LAYOUT;
const config = loadPageConfig();
const Title = lazy(() => import(`globalcomponents/Title`));
const Content = lazy(() => import(`@/${config.completedInspections[1].name}`));
const TopActions =
  config.completedInspections[0].children && lazy(() => import(`${config.completedInspections[0].children.name}`));
const Tab = lazy(() => import(`globalcomponents/UI/Tab/${layout}`));
const isServiceManager = hasRestrictedRole([ROLES.SERVICE_MANAGER]);

const Inspections = (parentProps) => {
  const { fetchInspections, getFilters, fetchDriverUserListing, getDocumentToken, history, i18n, theme } = parentProps;
  const commonParentProps = { history, i18n, Styled, theme };

  const [pagination, setPagination] = useState(false);
  const [auth, setAuth] = useState(null);

  useEffect(() => {
    const token = getCookie(COOKIE_AUTH_DETAILS);
    if (token) {
      setAuth(token);
    }
  }, []);

  const [request, setRequest] = useState({
    ...basicRequest,
  });

  const changeRequest = (fields) => {
    if (fields?.sortCol) {
      const type = fields.sortCol;
      fields.sortCol = sortColumnMapping[type] ? sortColumnMapping[type] : fields.sortCol;
    }
    setRequest({ ...basicRequest, ...fields });
  };

  const searchConfig = {
    setPagination,
    searchPlaceholder: 'Search Inspection Logs',
    changeRequest,
    fromData: false,
  };

  useEffect(() => {
    const user = getCookie(COOKIE_AUTH_DETAILS);
    let requestToModify = { ...request };
    if (hasRestrictedRole([ROLES.MECHANIC])) {
      requestToModify = { ...request, mechanicId: user?.userId };
    } else if (hasRestrictedRole([ROLES.DRIVER])) {
      requestToModify = { ...request, driverId: user?.userId };
    }
    fetchInspections({
      apiId: 'fetchPendingMechanicInspections',
      data: { request: requestToModify, state: 'CLOSED' },
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request]);

  useEffect(() => {
    fetchDriverUserListing({
      listApiId: 'fetchDriverUser',
    });
    getFilters({ apiId: 'getFilters' });
    getDocumentToken({ apiId: 'getDocToken' });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isCustomDviRequired = hasEnabledFeature('customDvirEnabled') && !isServiceManager;
  const isAdvancedDviRequired = hasEnabledFeature('advancedDvirEnabled') && !isServiceManager;

  return (
    <>
      {!auth?.enterpriseFeatureDto?.dvirOnlyEnabled ? (
        <RenderComponent
          {...config.inspections[0]}
          parentProps={{
            ...parentProps,
            isAdvancedDviRequired,
            isCustomDviRequired,
            changeRequest,
            request,
            pagination,
            setPagination,
          }}
          commonParentProps={commonParentProps}
          component={Title}
          childComponent={TopActions}
          searchConfig={window.$environment.CURRENT_LAYOUT === 'SpireonNew' ? searchConfig : null}
          noBackbutton={true}
        />
      ) : null}
      {auth?.enterpriseFeatureDto?.dvirOnlyEnabled ? (
        <RenderComponentTab
          {...config.inspections[1]}
          parentProps={{
            ...parentProps,
            isAdvancedDviRequired,
            isCustomDviRequired,
            changeRequest,
            request,
            pagination,
            setPagination,
          }}
          commonParentProps={commonParentProps}
          component={Content}
          history={history}
          i18n={i18n}
          pagination={pagination}
          setPagination={setPagination}
        />
      ) : (
        <RenderComponent
          {...config.inspections[1]}
          parentProps={{
            ...parentProps,
            isAdvancedDviRequired,
            isCustomDviRequired,
            changeRequest,
            request,
            pagination,
            setPagination,
          }}
          commonParentProps={commonParentProps}
          component={Content}
        />
      )}
    </>
  );
};

const RenderComponentTab = ({
  name,
  props,
  commonProps,
  title,
  parentProps,
  commonParentProps,
  component,
  history,
  i18n,
  verticalTab,
  selectedIndex,
  setSelectedIndexfn,
  pagination,
  setPagination,
  ...rest
}) => {
  const Component = component;
  const handleChange = (event, newValue) => {
    history.push(tabValues[newValue].url);
  };

  let newProps = props?.length ? getNewObject(parentProps, props) : {};
  newProps = {
    ...newProps,
    ...(commonProps?.length ? getNewObject(commonParentProps, commonProps) : {}),
    ...rest,
  };

  const tabValues = useMemo(() => {
    const sharedValue = (
      <Component {...newProps} selectedIndex={selectedIndex} pagination={pagination} setPagination={setPagination} />
    );
    let visibleTabs = [...allTabs];

    if (isServiceManager || hasRestrictedRole([ROLES.MECHANIC])) {
      visibleTabs.pop();
    } else {
      visibleTabs.shift();
    }
    if (hasRestrictedRole([ROLES.MECHANIC])) {
      visibleTabs.shift();
    }

    return visibleTabs.map((tab) => ({
      name: tab.name,
      value: sharedValue,
      url: pageUrls[tab.key],
    }));
  }, [newProps, selectedIndex, pagination, setPagination]);

  return (
    <Styled.Content>
      <Tab
        data={tabValues}
        className="logwrapper"
        currentTab={isServiceManager ? 3 : 2}
        onChange={handleChange}
        vertical={true}
      />
    </Styled.Content>
  );
};
const mapState = (state) => {
  return {
    apiStatus: state.asyncRequests,
    inspections: state.pendingMechanicInspections,
    list: state.inspections,
    filters: state.filters,
    logBook: state.logBook,
  };
};
const mapDispatch = (dispatch) => {
  return {
    fetchInspections: (data) => dispatch.pendingMechanicInspections.fetchData(data),
    bulkDownloadReport: (data) => dispatch.logBook.downloadData(data),
    fetchDriverUserListing: (data) => dispatch.inspections.fetchData(data),
    getFilters: (data) => dispatch.filters.fetchData(data),
    getDocumentToken: (data) => dispatch.logBook.documentToken(data),
    clearApiStatus: () => dispatch.asyncRequests.clearApiStatus(),
    viewReport: (data) => dispatch.viewReport.viewReportDocument(data),
  };
};

export default connect(mapState, mapDispatch)(Inspections);
Inspections.propTypes = {
  apiStatus: PropTypes.object.isRequired,
  fetchInspections: PropTypes.func.isRequired,
};
